import datacentral from '@/utils/axios/datacentral'
import { ResponseObject, ResponseArray } from '@/types/shared'
import {
    DraftAsset,
    AssetVersion,
    AssetDetail,
    AssetVersionListItem,
} from '../types'
import qs from 'query-string'
import { DataGridPayload } from 'vnetwork-ui/dist/components/GokuDataGrid'

// Draft creation payload
export interface CreateAssetDraftPayload {
    asset_name: string
    asset_description?: string
    document_group: string
    document_type: string
    confidentiality_level: string
    integrity_level: string
    availability_level: string
    department_id: number
    // Sharing settings for private share selection
    shared_employee_ids?: number[]
    shared_department_ids?: number[]
    start_date?: string
    end_date?: string
    is_read_confirmation_required?: boolean
    is_download_restricted?: boolean
    notes?: string
    notify_scope?: 'none' | 'all' | 'custom'
    notify_department_ids?: number[]
    notify_employee_ids?: number[]
}

// Draft operations
export const createAssetDraft = async (data: CreateAssetDraftPayload) => {
    const response = await datacentral.post<ResponseObject<DraftAsset>>(
        '/assets/draft',
        data
    )
    return response.data
}

// Update asset payload
export interface UpdateAssetPayload extends Partial<CreateAssetDraftPayload> {
    asset_version_id?: string
}

// Update asset (draft or otherwise)
export const updateAsset = async (id: string, data: UpdateAssetPayload) => {
    const response = await datacentral.put<ResponseObject<DraftAsset>>(
        `/assets/${id}`,
        data
    )
    return response.data
}

// File upload interfaces
export interface PresignedUrlRequest {
    file_name: string
    file_type: string
    is_temporary: boolean
}

export interface PresignedUrlResponse {
    presigned_url: string
    file_name: string
    is_temporary: boolean
    temp_path: string
    required_headers: Record<string, string>
}

export interface UploadedFile {
    file_name: string
    file_path: string // This will store the temp_path from API
    file_size: number
    file_type: string
    uploaded_at: string
    /** Present when file comes from an existing asset version */
    asset_file_id?: string
}

// Get presigned URL for file upload
export const getPresignedUrl = async (data: PresignedUrlRequest) => {
    const response = await datacentral.post<
        ResponseObject<PresignedUrlResponse>
    >('/assets/upload/presigned-url', data)
    return response.data
}

// Upload file to presigned URL
export const uploadFileToPresignedUrl = async (
    uploadUrl: string,
    file: File
) => {
    try {
        const headers: Record<string, string> = {
            'Content-Type': file.type || 'application/octet-stream',
            'x-amz-meta-expiration-time': '1769418556',
        }

        console.log(headers)
        // Use fetch with minimal headers to avoid CORS preflight
        const response = await fetch(uploadUrl, {
            method: 'PUT',
            body: file,
            headers,
        })

        if (!response.ok) {
            throw new Error(
                `Failed to upload file: ${response.status} ${response.statusText}`
            )
        }

        return response
    } catch (error) {
        // If CORS error persists, try with FormData as fallback
        if (error instanceof Error && error.message.includes('CORS')) {
            console.warn('CORS error detected, trying FormData approach...')

            const formData = new FormData()
            formData.append('file', file)

            const fallbackResponse = await fetch(uploadUrl, {
                method: 'PUT',
                body: formData,
            })

            if (!fallbackResponse.ok) {
                throw new Error(
                    `Failed to upload file (fallback): ${fallbackResponse.status} ${fallbackResponse.statusText}`
                )
            }

            return fallbackResponse
        }

        throw error
    }
}

// Asset submission interfaces
export interface AssetSubmitPayload {
    asset_id: string
    asset_version_id: string
    file_paths: string[]
    // Include all the form data fields
    asset_name: string
    asset_description?: string
    document_group: string
    document_type: string
    confidentiality_level: string
    integrity_level: string
    availability_level: string
    department_id: number
    // Sharing settings for private share selection
    shared_employee_ids?: number[]
    shared_department_ids?: number[]
    start_date?: string
    end_date?: string
    is_read_confirmation_required?: boolean
    is_download_restricted?: boolean
    notes?: string
    // Notification recipients on publish
    notify_scope?: 'none' | 'all' | 'custom'
    notify_department_ids?: number[]
    notify_employee_ids?: number[]
}

export interface AssetSubmitResponse {
    asset_id: string
    asset_version_id: string
    status: 'PROCESSING' | string
    message: string
    files_count?: number
}

// Submit final asset
export const submitAsset = async (data: AssetSubmitPayload) => {
    const response = await datacentral.post<
        ResponseObject<AssetSubmitResponse>
    >('/assets/submit', data)
    return response.data
}

// Create new version for a published asset
export interface CreateAssetVersionPayload {
    asset_name: string
    asset_description?: string
    document_group: string
    document_type: string
    department_id: number
    confidentiality_level: string
    integrity_level: string
    availability_level: string
    start_date?: string | null
    end_date?: string | null
    is_read_confirmation_required?: boolean
    is_download_restricted?: boolean
    notes?: string
    version_bump_type: 'major' | 'minor' | 'patch'
    file_paths?: string[]
    existing_file_ids?: string[]
    shared_employee_ids?: number[]
    shared_department_ids?: number[]
    // Notification recipients on publish
    notify_scope?: 'none' | 'all' | 'custom'
    notify_department_ids?: number[]
    notify_employee_ids?: number[]
}

export interface CreateAssetVersionResponse {
    asset_id: string
    asset_version_id: string
    version_code: string
    major: number
    minor: number
    patch: number
    asset_version_status: 'PROCESSING' | string
    files_count?: number
}

// Create new asset version (update published asset)
export const createAssetVersion = async (
    assetId: string,
    data: CreateAssetVersionPayload
) => {
    const response = await datacentral.post<
        ResponseObject<CreateAssetVersionResponse>
    >(`/assets/${assetId}/versions`, data)
    return response.data
}

export interface AssetListItem {
    asset_id: string
    asset_name: string
    asset_slug: string
    document_group: string
    asset_description: string
    created_by: number
    created_by_name: string
    current_version_id: string | null
    current_version: AssetVersion | null
    processing_version_id: string | null
    department_name: string
    created_at: string
    updated_at: string
    total_files: number
    status: string
}

// Get assets list
export const getAssets = async (
    payload: DataGridPayload & { status?: string }
): Promise<ResponseArray<AssetListItem>> => {
    const response = await datacentral.get(`/assets?${qs.stringify(payload)}`)
    return response.data
}

// Get submitted assets (pending approval)
export const getSubmittedAssets = async (
    payload: DataGridPayload
): Promise<ResponseArray<AssetListItem>> => {
    const response = await datacentral.get(
        `/assets/submitted?${qs.stringify(payload)}`
    )
    return response.data
}

// Get my assets (created by current user)
export const getMyAssets = async (
    payload: DataGridPayload
): Promise<ResponseArray<AssetListItem>> => {
    const response = await datacentral.get(
        `/assets/my?${qs.stringify(payload)}`
    )
    return response.data
}

// Get asset versions
export const getAssetVersions = async (
    assetId: string
): Promise<ResponseArray<AssetVersionListItem>> => {
    const response = await datacentral.get(`/assets/${assetId}/versions`)
    return response.data
}

// Get single asset detail
export const getAssetDetail = async (
    slug: string,
    versionId?: string
): Promise<ResponseObject<AssetDetail>> => {
    const params = versionId ? `?version_id=${versionId}` : ''
    const response = await datacentral.get(`/assets/slug/${slug}${params}`)
    return response.data
}

/**
 * Get a short-lived presigned URL for one asset file.
 *
 * Assets deliberately do NOT reuse the drive presign endpoint: that one accepts an arbitrary
 * S3 key and cannot enforce a document's download restriction. This route takes a file id and
 * authorises it server-side.
 *
 * `mode: 'download'` additionally enforces the restriction and returns 403
 * `asset-download-restricted` when the caller is neither the owner nor an admin.
 */
export const getAssetFileUrl = async (
    assetFileId: string,
    mode: 'view' | 'download' = 'view'
): Promise<ResponseObject<string>> => {
    const response = await datacentral.get(
        `/assets/files/${assetFileId}/url`,
        { params: { mode } }
    )
    return response.data
}

// Delete asset
export const deleteAsset = async (
    slug: string
): Promise<ResponseObject<any>> => {
    const response = await datacentral.delete(`/assets/${slug}`)
    return response.data
}

// Delete draft asset
export const deleteDraftAsset = async (
    id: string
): Promise<ResponseObject<any>> => {
    const response = await datacentral.delete(`/assets/drafts/${id}`)
    return response.data
}

// Get pending drafts
export const getMyDraftAssets = async (
    payload: DataGridPayload
): Promise<ResponseArray<AssetListItem>> => {
    const response = await datacentral.get(
        `/assets/drafts?${qs.stringify(payload)}`
    )
    return response.data
}

// Get draft detail
export const getDraftAssetDetail = async (
    id: string
): Promise<ResponseObject<AssetDetail>> => {
    const response = await datacentral.get(`/assets/drafts/${id}`)
    return response.data
}

// Approval payload (comment is optional)
export interface ApproveAssetPayload {
    asset_version_id: string
    comment?: string
}

// Rejection payload (comment is required)
export interface RejectAssetPayload {
    asset_version_id: string
    comment: string
}

// Approve asset by Manager
export const approveAssetByManager = async (
    data: ApproveAssetPayload
): Promise<ResponseObject<any>> => {
    const response = await datacentral.post('/assets/manager/approve', data)
    return response.data
}

// Approve asset by BOD
export const approveAssetByBOD = async (
    data: ApproveAssetPayload
): Promise<ResponseObject<any>> => {
    const response = await datacentral.post('/assets/bod/approve', data)
    return response.data
}

// Reject asset by Manager
export const rejectAssetByManager = async (
    data: RejectAssetPayload
): Promise<ResponseObject<any>> => {
    const response = await datacentral.post('/assets/manager/reject', data)
    return response.data
}

// Reject asset by BOD
export const rejectAssetByBOD = async (
    data: RejectAssetPayload
): Promise<ResponseObject<any>> => {
    const response = await datacentral.post('/assets/bod/reject', data)
    return response.data
}

// Reject asset (generic - kept for compatibility if needed, but UI should prefer specific ones)
export const rejectAsset = async (
    data: RejectAssetPayload
): Promise<ResponseObject<any>> => {
    const response = await datacentral.post('/assets/reject', data)
    return response.data
}

// Get dashboard assets
export const getAssetDashboard = async (documentGroup: string[]) => {
    const response = await datacentral.get<ResponseArray<AssetDetail>>(
        `/assets/dashboard?${qs.stringify({
            document_group: documentGroup,
        })}`
    )
    return response.data
}
